import { BaseSyntheticEvent, useCallback, useMemo, useState } from 'react';
import {
	Typography,
	Grid,
	TextField,
	FormControl,
	Input,
	InputAdornment,
	Box,
	MenuItem,
	Button,
	Avatar,
	ListItem,
	ListItemAvatar,
	ListItemText,
	LinearProgress,
} from '@material-ui/core';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
	FixedSizeList,
	ListChildComponentProps,
	ListOnItemsRenderedProps,
} from 'react-window';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import 'moment/locale/he';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { BsSearch } from 'react-icons/bs';
import { BiSortDown, BiSortUp } from 'react-icons/bi';
import { MdClear } from 'react-icons/md';
import { FaCheck } from 'react-icons/fa';
import { HiUserGroup } from 'react-icons/hi';
import './Groups.styles.css';
import { BsDot } from 'react-icons/bs';
import { BsFilter } from 'react-icons/bs';
import {
	GroupsProps,
	testGroupDataProps,
} from '../../Types/WhatsappCampaign.types';
import { coreProps } from '../../../Editor/Types/WhatsappCreator.types';
import {
	PREFETCH_ROWS,
	SEARCH_DEBOUNCE_MS,
	useServerPagedGroups,
} from '../../../../../hooks/useServerPagedGroups';
import ProgressiveTags from '../../../../../components/Groups/GroupsHandler/ProgressiveTags';
import GroupsList from './Component/GroupsList';
import GroupsSelectAll from './Component/GroupsSelectAll';
import { DefaultGroupSort, sortGroupsByUpdateDate } from '../../../../../helpers/Utils/groupSortUtils';

// PR-3790: row pitch for the virtual list. Must match the rendered row height.
const ITEM_HEIGHT = 54;
// PR-3790: extra rows rendered above/below the viewport for smooth scrolling.
const OVERSCAN_COUNT = 5;
const DEFAULT_LIST_HEIGHT = 325;

const Groups = ({
	classes,
	list,
	isFilterSelected,
	selectedList,
	innerHeight,
	showSortBy,
	showFilter,
	showSelectAll,
	callbackSelectedGroups,
	callbackUpdateGroups,
	callbackSelectAll,
	callbackReciFilter,
	callbackShowTestGroup,
	uniqueKey,
	showTestGroups,
	fetchPage,
	pinnedRows,
	reloadToken
}: GroupsProps) => {
	const { windowSize, isRTL } = useSelector(
		(state: { core: coreProps }) => state.core
	);
	const { t: translator } = useTranslation();

	// `searchInput` keeps the field controlled; `groupNameSearch` is the
	// debounced value the in-memory list filters on. In paged mode the hook owns
	// both instead, so the server does the filtering.
	const [searchInput, setSearchInput] = useState<string>('');
	const [groupNameSearch, setGroupNameSearch] = useState<string>('');
	// Default sort: UpdateDate DESC (SortByField 'UpdateDate', SortDirection 0).
	const [sortBySelected, setSortBy] = useState<string>(DefaultGroupSort.FIELD);
	const [sortDirection, setSortDirection] = useState<string>(DefaultGroupSort.DIRECTION);

	// PR-3790: paging is opt-in. Without `fetchPage` this hook is inert and the
	// picker behaves exactly as it did before.
	const paged = useServerPagedGroups({
		fetchPage,
		// Test groups arrive via pinnedRows from their own endpoint; asking the server
		// for them too would list each one twice. The old endpoint also filtered them out.
		showTestGroups: false,
		sortBySelected,
		sortDirection,
		reloadToken,
	});

	const debouncedSearch = useMemo(
		() => debounce((value: string) => setGroupNameSearch(value), SEARCH_DEBOUNCE_MS),
		[]
	);

	const handleShowTestGroup = () => {
		callbackShowTestGroup(showTestGroups);
	};

	const handleSearch = (event: BaseSyntheticEvent) => {
		const value = event.target.value;
		if (paged.enabled) {
			paged.onSearchChange(value);
			return;
		}
		setSearchInput(value);
		debouncedSearch(value);
	};

	const resetSearch = (_event: BaseSyntheticEvent) => {
		if (paged.enabled) {
			paged.resetSearch();
			return;
		}
		debouncedSearch.cancel();
		setSearchInput('');
		setGroupNameSearch('');
	};

	const onSelectGroup = useCallback(
		(group: testGroupDataProps) => {
			callbackSelectedGroups(group);
		},
		[callbackSelectedGroups]
	);

	const onTagChange = (value: testGroupDataProps[]) => {
		callbackUpdateGroups(value);
	};

	const defaultProps = {
		options: selectedList,
		getOptionLabel: (group: testGroupDataProps) => {
			if (group) {
				return group.GroupName;
			}
			return '';
		},
	};

	const onSelectAllGroup = useCallback(() => {
		// In paged mode the caller needs the current filter, so it can ask the
		// server for exactly the set the list is showing rather than copying
		// downloaded objects it no longer has.
		callbackSelectAll(paged.enabled ? paged.filter : undefined);
	}, [callbackSelectAll, paged.enabled, paged.filter]);

	const groupSortOptions = [
		{
			value: 'Group Name',
			text: translator('notifications.sort_by_group'),
		},
		{
			value: 'Creation Date',
			text: translator('notifications.sort_by_creation'),
		},
		{
			value: 'Update Date',
			text: translator('notifications.sort_by_updated'),
		},
	];

	const renderSortItems = () => {
		return groupSortOptions.map((sortBy) => {
			return (
				<MenuItem key={sortBy.value} value={sortBy.value}>
					{sortBy.text}
				</MenuItem>
			);
		});
	};

	const handleSortBySelected = (event: SelectChangeEvent) => {
		setSortBy(event.target.value);
	};

	const handleSortDirection = () => {
		setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
	};

	// PR-3790: search + sort derived from state over a COPY of `list`.
	// The previous implementation called list.sort() imperatively, which
	// mutated the array owned by the caller.
	const processedList = useMemo(() => {
		if (!list || list.length === 0) return [];

		let tempList = [...list];

		const term = groupNameSearch.trim().toLowerCase();
		if (term) {
			tempList = tempList.filter((group: testGroupDataProps) =>
				group.GroupName?.toLowerCase().includes(term)
			);
		}

		const direction = sortDirection === 'asc' ? 1 : -1;
		if (sortBySelected === 'Group Name') {
			tempList.sort(
				(a: testGroupDataProps, b: testGroupDataProps) =>
					(a.GroupName || '').toUpperCase().localeCompare((b.GroupName || '').toUpperCase()) *
					direction
			);
		} else if (sortBySelected === 'Update Date') {
			tempList.sort((a: testGroupDataProps, b: testGroupDataProps) => {
				const aDate = a.UpdateDate ? Date.parse(a.UpdateDate) : 0;
				const bDate = b.UpdateDate ? Date.parse(b.UpdateDate) : 0;
				return (aDate - bDate) * direction;
			});
		} else if (sortBySelected === 'Creation Date') {
			tempList.sort((a: testGroupDataProps, b: testGroupDataProps) => {
				const aDate = a.CreationDate ? Date.parse(a.CreationDate) : 0;
				const bDate = b.CreationDate ? Date.parse(b.CreationDate) : 0;
				return (aDate - bDate) * direction;
			});
		}

		return tempList;
	}, [list, groupNameSearch, sortBySelected, sortDirection]);

	const selectedIds = useMemo(
		() =>
			new Set(
				(selectedList || []).map(
					(group: testGroupDataProps) => group.GroupID ?? group.WACampaignID
				)
			),
		[selectedList]
	);

	// PR-3790: in paged mode the rows come from the server. Test groups arrive
	// from a separate endpoint, so they are pinned above the paged results
	// rather than being concatenated into the paged set.
	const displayList = useMemo(() => {
		if (!paged.enabled) return processedList;
		const pinned = pinnedRows || [];
		return pinned.length > 0 ? pinned.concat(paged.rows) : paged.rows;
	}, [paged.enabled, paged.rows, pinnedRows, processedList]);

	// How many groups match the current filter in total. Paged mode gets this
	// from the server, because `list` is now only the pages loaded so far.
	const totalMatching = paged.enabled
		? paged.recordCount + (pinnedRows?.length || 0)
		: list?.length || 0;

	const allSelected = totalMatching > 0 && (selectedList?.length || 0) === totalMatching;

	const listHeight =
		typeof innerHeight === 'number' && innerHeight > 0 ? innerHeight : DEFAULT_LIST_HEIGHT;

	const showLoadingRow = paged.enabled && paged.isLoading;
	const totalItems =
		displayList.length + (showSelectAll ? 1 : 0) + (showLoadingRow ? 1 : 0);

	// PR-3790: load the next page once the last loaded row scrolls into view.
	// Row 0 is "Select All" when shown, so the last group row sits one further down.
	const handleItemsRendered = useCallback(
		({ visibleStopIndex }: ListOnItemsRenderedProps) => {
			if (!paged.enabled || !paged.hasMore || paged.isLoading) return;
			const lastRowIndex = displayList.length - 1 + (showSelectAll ? 1 : 0);
			if (visibleStopIndex >= lastRowIndex - PREFETCH_ROWS) {
				paged.loadMore();
			}
		},
		[paged, displayList.length, showSelectAll]
	);

	// PR-3790: only the rows inside the viewport are mounted. Previously every
	// group in the account was rendered as a MUI ListItem at once.
	const RowRenderer = useCallback(
		({ index, style }: ListChildComponentProps) => {
			if (showSelectAll && index === 0) {
				return (
					<div style={style}>
						<SelectAllRow
							classes={classes}
							isRTL={isRTL}
							allSelected={allSelected}
							label={translator('notifications.selectAll')}
							onSelectAllGroup={onSelectAllGroup}
						/>
					</div>
				);
			}

			const group = displayList[showSelectAll ? index - 1 : index];
			if (!group) {
				// Trailing slot while the next page is in flight. Matches the
				// loader the WhatsApp chat contact list already uses.
				return (
					<div style={style}>
						{showLoadingRow && (
							<Box width='100%' paddingTop='24px'>
								<LinearProgress />
							</Box>
						)}
					</div>
				);
			}

			return (
				<div style={style}>
					<GroupRow
						classes={classes}
						isRTL={isRTL}
						group={group}
						isSelected={selectedIds.has(group.GroupID)}
						onSelectGroup={onSelectGroup}
						recipientLabel={
							group.Recipients !== 1
								? translator('notifications.recipients')
								: translator('notifications.recipient')
						}
					/>
				</div>
			);
		},
		[
			displayList,
			selectedIds,
			showSelectAll,
			showLoadingRow,
			allSelected,
			classes,
			isRTL,
			onSelectGroup,
			onSelectAllGroup,
			translator,
		]
	);

	const searchField = (
		<FormControl className={clsx(classes.margin, classes.searchInput)}>
			<Input
				onChange={handleSearch}
				placeholder={translator('notifications.buttons.search')}
				value={paged.enabled ? paged.searchInput : searchInput}
				startAdornment={
					<InputAdornment position='start'>
						<BsSearch />
					</InputAdornment>
				}
				endAdornment={
					(paged.enabled ? paged.searchInput : searchInput)?.length > 0 && (
						<InputAdornment position='start' onClick={resetSearch}>
							<MdClear style={{ cursor: 'pointer' }} />
						</InputAdornment>
					)
				}
			/>
		</FormControl>
	);

	return (
		<Box className={classes.groupsContainer} key={uniqueKey}>
			{windowSize === 'xs' && (
				<Grid item xs={12}>
					{searchField}
				</Grid>
			)}
			<Grid
				item
				xs={12}
				className={clsx(classes.flex, classes.groupFilterRow)}
				style={{ whiteSpace: windowSize !== 'xs' ? 'nowrap' : 'normal' }}>
				{windowSize !== 'xs' && searchField}
				{showSortBy && (
					<Box className={classes.filterButtonsContainer}>
						{selectedList.length > 0 && showFilter ? (
							<Button
								className={clsx(classes.formControl, classes.whatsappDropDown, classes.mt1)}
								onClick={callbackReciFilter}
								style={{
									height: '36px',
									color: '#ff3343',
									fontWeight: '600',
									textTransform: 'capitalize',
								}}>
								{windowSize !== 'xs' && (
									<BsFilter style={{ fontSize: '22px', color: '#ff3343' }} />
								)}{' '}
								{isFilterSelected ? (
									<BsDot
										style={{
											position: 'absolute',
											left: '8px',
											top: '-6px',
											fontSize: '28px',
										}}
									/>
								) : null}{' '}
								<>{translator('mainReport.recipientFilter')}</>
							</Button>
						) : null}
						<Button
							variant='outlined'
							className={clsx(
								classes.formControl,
								classes.mt1,
								showTestGroups
									? classes.buttonActiveRed
									: classes.twoLineButton
							)}
							onClick={() => handleShowTestGroup()}>
							<>{translator('sms.showTestGroups')}</>
						</Button>
						<FormControl
							className={clsx(classes.whatsappDropDown, classes.mt1)}
						>
							<Select
								variant="standard"
								id='groupOrder'
								value={sortBySelected}
								onChange={handleSortBySelected}
								endAdornment={null}
								className={clsx(classes.paddingSides10)}
								style={{ color: 'inherit', fontSize: '0.875rem', lineHeight: '1.5rem' }}
								MenuProps={{
									PaperProps: {
										style: {
											maxHeight: 300,
											direction: isRTL ? 'rtl' : 'ltr'
										},
									},
								}}
							>
								{renderSortItems()}
							</Select>
						</FormControl>
						<Button
							style={{
								margin:
									selectedList.length > 0 && showFilter && windowSize === 'xs'
										? '5px 0px'
										: '',
							}}
							className={clsx(
								classes.formControl,
								classes.whatsappDropDown,
								classes.controlField,
								classes.mt1
							)}
							onClick={() => {
								handleSortDirection();
							}}>
							{sortDirection === 'asc' ? <BiSortDown /> : <BiSortUp />}
						</Button>
					</Box>
				)}
			</Grid>
			<Autocomplete
				{...defaultProps}
				multiple
				id='multiple-limit-tags'
				value={selectedList}
				getOptionSelected={(
					option: testGroupDataProps,
					value: testGroupDataProps
				) => option.GroupID === value.GroupID}
				getOptionLabel={(group) => group.GroupName || ''}
				renderTags={(value, getTagProps) => (
					<ProgressiveTags
						value={value}
						getTagProps={getTagProps}
						getLabel={(group: testGroupDataProps) => group.GroupName || ''}
					/>
				)}
				defaultValue={[]}
				open={false}
				popupIcon={false}
				onChange={(_e, value: Array<testGroupDataProps>) => onTagChange(value)}
				renderInput={(params) =>
					selectedList.length > 0 ? (
						<TextField
							{...params}
							className={clsx(
								classes.bottomShadow,
								classes.tagSelected,
								classes.sidebar
							)}
							style={{ maxHeight: 45 }}></TextField>
					) : (
						<Typography
							className={clsx(classes.bottomShadow, classes.noSelection)}>
							<>{translator('notifications.noGroupsSelected')}</>
						</Typography>
					)
				}
			/>
			<div className={clsx(classes.demo, classes.sidebar)}>
				<FixedSizeList
					key={uniqueKey}
					height={listHeight}
					itemCount={totalItems}
					itemSize={ITEM_HEIGHT}
					width='100%'
					overscanCount={OVERSCAN_COUNT}
					onItemsRendered={handleItemsRendered}
					style={{ direction: isRTL ? 'rtl' : 'ltr' }}
				>
					{RowRenderer}
				</FixedSizeList>
			</div>
		</Box>
	);
};

type SelectAllRowProps = {
	classes: GroupsProps['classes'];
	isRTL: boolean;
	allSelected: boolean;
	label: string;
	onSelectAllGroup: () => void;
};

const SelectAllRow = ({
	classes,
	isRTL,
	allSelected,
	label,
	onSelectAllGroup,
}: SelectAllRowProps) => (
	<ListItem
		className={clsx(classes.groupListRow, 'group-container')}
		onClick={() => onSelectAllGroup()}
		style={{ cursor: 'pointer' }}>
		<ListItemAvatar>
			<Avatar
				className={clsx(
					classes.listIcon,
					classes.transparentBg,
					allSelected ? classes.redBg : classes.transparentBg,
					allSelected ? classes.borderRed : classes.borderPrimary
				)}>
				{allSelected ? (
					<FaCheck className={clsx(classes.white)} />
				) : (
					<HiUserGroup className={clsx(classes.colrPrimary)} />
				)}
			</Avatar>
		</ListItemAvatar>
		<Box dir={isRTL ? 'rtl' : 'ltr'} width='100%'>
			<ListItemText
				className={clsx('groupText', !isRTL && classes.textLeft)}
				title={label}
				primary={label}
			/>
		</Box>
	</ListItem>
);

type GroupRowProps = {
	classes: GroupsProps['classes'];
	isRTL: boolean;
	group: testGroupDataProps;
	isSelected: boolean;
	recipientLabel: string;
	onSelectGroup: (group: testGroupDataProps) => void;
};

const GroupRow = ({
	classes,
	isRTL,
	group,
	isSelected,
	recipientLabel,
	onSelectGroup,
}: GroupRowProps) => (
	<ListItem
		onClick={() => onSelectGroup(group)}
		className={clsx(classes.groupListRow, 'group-container')}
		style={{ cursor: 'pointer' }}
	>
		<ListItemAvatar>
			<Avatar
				className={clsx(
					classes.listIcon,
					isSelected ? classes.redBg : classes.transparentBg,
					isSelected ? classes.white : classes.blue,
					isSelected ? classes.borderRed : classes.borderPrimary
				)}>
				{isSelected ? (
					<FaCheck className={clsx(classes.white)} />
				) : (
					<HiUserGroup className={clsx(classes.colrPrimary)} />
				)}
			</Avatar>
		</ListItemAvatar>
		<Box dir={isRTL ? 'rtl' : 'ltr'} width='100%'>
			<ListItemText
				className={clsx('groupText', !isRTL && classes.textLeft)}
				title={group.GroupName}
				primary={group.GroupName}
			/>
		</Box>
		<Box dir={isRTL ? 'rtl' : 'ltr'} width='100%'>
			<Box
				className={clsx(
					'groupText',
					classes.itemAvatar,
					isRTL ? classes.textLeft : classes.textRight
				)}>
				{group.Recipients?.toLocaleString()} {recipientLabel}
			</Box>
		</Box>
	</ListItem>
);

export default Groups;
